import { useCallback, useState } from "react";

import { ChevronDownIcon } from "@vericus/cadmus-icons";
import { Command, DropdownMenu, Text } from "@vericus/cadmus-ui";

import { Row } from "@tanstack/table-core";
import { clsx } from "clsx";

import { IssueStatus, ProjectIssueInput } from "@/generated/graphql";

import { issueStatuses } from "../../data";
import { useUpdateProjectIssue } from "../../hooks/use-update-project-issue";
import { ProjectIssueWithChildren } from "../../types";
import { getStatusBadgeStyles } from "../../utils";
import { statusBadge } from "../filter-combobox/filter-combobox.css";
import { useProjectManagementTableContext } from "../table";
import * as styles from "./styles.css";

interface StatusDropdownProps {
  row: Row<ProjectIssueWithChildren>;
}

export const StatusDropdownWithRowData = ({ row }: StatusDropdownProps) => {
  const { id: issueId, groupId } = row.original;

  const updateProjectIssue = useUpdateProjectIssue(groupId);

  const { table } = useProjectManagementTableContext();

  const [open, setOpen] = useState(false);

  const handleUpdateStatus = useCallback(
    (issueStatus: IssueStatus) => {
      const currentIssue = table
        .getRowModel()
        .rows.find((row) => row.original.id === issueId)?.original;
      // to prevent unnecessary compute
      if (currentIssue?.status === issueStatus) return;

      updateProjectIssue([
        {
          id: issueId,
          status: issueStatus,
        } as ProjectIssueInput,
      ]);
    },
    [issueId, table, updateProjectIssue]
  );

  return (
    <StatusDropdown
      open={open}
      status={row.original.status}
      setOpen={setOpen}
      handleUpdateStatus={handleUpdateStatus}
    />
  );
};

export function StatusDropdown({
  open,
  setOpen,
  status,
  handleUpdateStatus,
}: {
  open: boolean;
  status: IssueStatus;

  setOpen: (open: boolean) => void;
  handleUpdateStatus: (status: IssueStatus) => void;
}) {
  const statusText = (status as string).replace("_", " ");
  return (
    <DropdownMenu.Root open={open} onOpenChange={(open) => setOpen(open)}>
      <DropdownMenu.Trigger asChild>
        <button
          className={clsx(styles.dropdownButton, getStatusBadgeStyles(status))}
        >
          <div className={styles.statusBadge}>
            <Text kind="label" as="div" color="inherit">
              {statusText}
            </Text>

            <ChevronDownIcon label="down" size={16} />
          </div>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <Command.Root
          style={{ display: "flex", rowGap: 10, height: "100%", width: 230 }}
        >
          <Command.List>
            {issueStatuses.map((issueStatus) => (
              <Command.RadioItem
                key={issueStatus}
                value={issueStatus.toString().replace("_", " ")}
                onSelect={() => {
                  handleUpdateStatus(issueStatus);
                  setOpen(false);
                }}
                data-selected={status === issueStatus ? "true" : undefined}
              >
                <StatusCell status={issueStatus} />
              </Command.RadioItem>
            ))}
          </Command.List>
        </Command.Root>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export function StatusCell({ status }: { status: IssueStatus }) {
  return (
    <Text kind="label">
      <span className={clsx(statusBadge, getStatusBadgeStyles(status))}>
        {status.replace("_", " ")}
      </span>
    </Text>
  );
}
